import React, { useEffect, useMemo, useState } from 'react';
import {
  Button,
  Card,
  DatePicker,
  Empty,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
} from 'antd';
import type { TableColumnsType } from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  FallOutlined,
  InfoCircleOutlined,
  PlusOutlined,
  RiseOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import { formatCompactPLN, formatCurrency } from '../utils/currency';
import { formatLocalizedNumber, formatLocalizedPercent } from '../utils/numberFormatting';
import { useFeedback } from '../utils/feedback';
import { getApiErrorMessage } from '../utils/apiError';
import {
  COMPANY_COST_CATEGORIES,
  COMPANY_COST_CURRENCIES,
  CompanyCostCategory,
  CompanyCostCurrency,
  CompanyCostEntry,
  CompanyCostSource,
  CompanyCostTemplate,
  CompanyCostsSummary,
  createCompanyCostEntry,
  createCompanyCostTemplate,
  deleteCompanyCostEntry,
  deleteCompanyCostTemplate,
  fetchCompanyCostTemplates,
  fetchCompanyCostsMonth,
  MAX_COMPANY_COST_AMOUNT,
  updateCompanyCostEntry,
  updateCompanyCostTemplate,
} from '../modules/companyCosts/api/companyCostsApi';
import { CATEGORY_COLORS } from '../modules/companyCosts/companyCostCategoryColors';
import './FixedCosts.css';

const { MonthPicker } = DatePicker;

const MONTH_FORMAT = 'YYYY-MM';

const SOURCE_TAG_COLOR: Record<CompanyCostSource, string> = {
  RECURRING: 'blue',
  ONE_TIME: 'default',
  KSEF: 'purple',
};

interface EntryFormValues {
  name: string;
  category: CompanyCostCategory;
  amount: number;
  currency: CompanyCostCurrency;
  invoiceNumber?: string;
  isPaid?: boolean;
  paymentDate?: Dayjs | null;
  notes?: string;
}

interface TemplateFormValues {
  name: string;
  category: CompanyCostCategory;
  amount: number;
  currency: CompanyCostCurrency;
  activeFrom: Dayjs;
  activeTo?: Dayjs | null;
  notes?: string;
}

const emptySummary: CompanyCostsSummary = { total: 0, byCategory: {}, entryCount: 0 };

const FixedCosts: React.FC = () => {
  const { t, i18n } = useTranslation(['fixedCosts', 'common']);
  const feedback = useFeedback();

  // ── "Koszty miesiąca" tab state ──────────────────────────────────
  const [month, setMonth] = useState<Dayjs>(dayjs());
  const [entries, setEntries] = useState<CompanyCostEntry[]>([]);
  const [summary, setSummary] = useState<CompanyCostsSummary>(emptySummary);
  const [entriesLoading, setEntriesLoading] = useState(false);

  // Previous-month summary, fetched solely to power the "vs poprzedni
  // miesiąc" delta satellite card. Idempotent GET, same endpoint as the
  // current month — kept in its own state so a slow/failed previous-month
  // fetch never blocks or taints the current month's data.
  const [prevSummary, setPrevSummary] = useState<CompanyCostsSummary | null>(null);
  const [prevLoading, setPrevLoading] = useState(false);

  const [entryModalOpen, setEntryModalOpen] = useState(false);
  const [editingEntry, setEditingEntry] = useState<CompanyCostEntry | null>(null);
  const [savingEntry, setSavingEntry] = useState(false);
  const [deletingEntryId, setDeletingEntryId] = useState<string | null>(null);
  const [entryForm] = Form.useForm<EntryFormValues>();
  const entryIsPaid = Form.useWatch('isPaid', entryForm);

  // ── "Koszty cykliczne" tab state ─────────────────────────────────
  const [templates, setTemplates] = useState<CompanyCostTemplate[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState<CompanyCostTemplate | null>(null);
  const [savingTemplate, setSavingTemplate] = useState(false);
  const [deletingTemplateId, setDeletingTemplateId] = useState<string | null>(null);
  const [togglingTemplateId, setTogglingTemplateId] = useState<string | null>(null);
  const [templateForm] = Form.useForm<TemplateFormValues>();

  const monthParam = useMemo(() => month.format(MONTH_FORMAT), [month]);
  const prevMonthParam = useMemo(() => month.subtract(1, 'month').format(MONTH_FORMAT), [month]);

  const compact = (amount: number) => formatCompactPLN(amount, i18n.language || 'pl');

  const loadMonth = async (targetMonth: string) => {
    setEntriesLoading(true);
    try {
      const response = await fetchCompanyCostsMonth(targetMonth);
      setEntries(response.entries);
      setSummary(response.summary);
    } catch {
      feedback.error(t('fixedCosts:month.loadFailed'));
    } finally {
      setEntriesLoading(false);
    }
  };

  const loadPrevMonth = async (targetMonth: string) => {
    setPrevLoading(true);
    try {
      const response = await fetchCompanyCostsMonth(targetMonth);
      setPrevSummary(response.summary);
    } catch {
      // Silent — the delta card degrades to "no data" rather than
      // interrupting the page with an error toast for a secondary figure.
      setPrevSummary(null);
    } finally {
      setPrevLoading(false);
    }
  };

  const loadTemplates = async () => {
    setTemplatesLoading(true);
    try {
      setTemplates(await fetchCompanyCostTemplates());
    } catch {
      feedback.error(t('fixedCosts:templates.loadFailed'));
    } finally {
      setTemplatesLoading(false);
    }
  };

  useEffect(() => {
    loadMonth(monthParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [monthParam]);

  useEffect(() => {
    loadPrevMonth(prevMonthParam);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prevMonthParam]);

  useEffect(() => {
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Entry (one-time cost) modal ──────────────────────────────────
  const openCreateEntry = () => {
    setEditingEntry(null);
    setEntryModalOpen(true);
  };

  const openEditEntry = (entry: CompanyCostEntry) => {
    setEditingEntry(entry);
    setEntryModalOpen(true);
  };

  // Field seeding must happen AFTER the modal is open: antd Modal mounts its
  // children lazily, so setFieldsValue called before the first open targets a
  // not-yet-connected form and the values are silently dropped (the Waluta
  // select rendered empty instead of PLN).
  useEffect(() => {
    if (!entryModalOpen) return;
    // Always reset first (clears the previous open's values), then seed for
    // edit. The form deliberately keeps antd's default preserve=true:
    // paymentDate lives under a conditional Form.Item (visible only when
    // isPaid), and preserve=false drops the seeded value while the field is
    // still unmounted.
    entryForm.resetFields();
    if (editingEntry) {
      entryForm.setFieldsValue({
        name: editingEntry.name,
        category: editingEntry.category,
        amount: editingEntry.originalAmount ?? editingEntry.amount,
        currency: (editingEntry.originalCurrency as CompanyCostCurrency) ?? 'PLN',
        invoiceNumber: editingEntry.invoiceNumber ?? undefined,
        isPaid: editingEntry.isPaid,
        paymentDate: editingEntry.paymentDate ? dayjs(editingEntry.paymentDate) : null,
        notes: editingEntry.notes ?? undefined,
      });
    }
  }, [entryModalOpen, editingEntry, entryForm]);

  const closeEntryModal = () => {
    if (savingEntry) return;
    setEntryModalOpen(false);
    setEditingEntry(null);
  };

  const handleEntrySubmit = async (values: EntryFormValues) => {
    setSavingEntry(true);
    try {
      const payload = {
        name: values.name,
        category: values.category,
        amount: Number(values.amount),
        currency: values.currency,
        invoiceNumber: values.invoiceNumber || null,
        isPaid: Boolean(values.isPaid),
        paymentDate: values.isPaid && values.paymentDate ? values.paymentDate.format('YYYY-MM-DD') : null,
        notes: values.notes || null,
      };
      if (editingEntry) {
        await updateCompanyCostEntry(editingEntry.id, payload);
        feedback.success(t('fixedCosts:messages.updateSuccess', { name: values.name }));
      } else {
        await createCompanyCostEntry({ ...payload, month: monthParam });
        feedback.success(t('fixedCosts:messages.createSuccess', {
          name: values.name,
          month: dayjs(monthParam, MONTH_FORMAT).format('MM/YYYY'),
        }));
      }
      setEntryModalOpen(false);
      setEditingEntry(null);
      await loadMonth(monthParam);
    } catch (error: any) {
      feedback.error(
        getApiErrorMessage(error, t, 'fixedCosts:messages.saveFailed', undefined, { name: values.name }),
      );
    } finally {
      setSavingEntry(false);
    }
  };

  const handleDeleteEntry = async (entry: CompanyCostEntry) => {
    setDeletingEntryId(entry.id);
    try {
      await deleteCompanyCostEntry(entry.id);
      feedback.success(t('fixedCosts:messages.deleteSuccess', {
        name: entry.name,
        month: dayjs(entry.month, MONTH_FORMAT).format('MM/YYYY'),
      }));
      await loadMonth(monthParam);
    } catch (error: any) {
      feedback.error(
        getApiErrorMessage(error, t, 'fixedCosts:messages.deleteFailed', undefined, { name: entry.name }),
      );
    } finally {
      setDeletingEntryId(null);
    }
  };

  // ── Template (recurring cost) modal ──────────────────────────────
  const openCreateTemplate = () => {
    setEditingTemplate(null);
    setTemplateModalOpen(true);
  };

  const openEditTemplate = (template: CompanyCostTemplate) => {
    setEditingTemplate(template);
    setTemplateModalOpen(true);
  };

  // Same lazy-mount seeding rule as the entry modal above.
  useEffect(() => {
    if (!templateModalOpen) return;
    templateForm.resetFields();
    if (editingTemplate) {
      templateForm.setFieldsValue({
        name: editingTemplate.name,
        category: editingTemplate.category,
        amount: editingTemplate.originalAmount ?? editingTemplate.amount,
        currency: (editingTemplate.originalCurrency as CompanyCostCurrency) ?? 'PLN',
        activeFrom: dayjs(editingTemplate.activeFrom, MONTH_FORMAT),
        activeTo: editingTemplate.activeTo ? dayjs(editingTemplate.activeTo, MONTH_FORMAT) : null,
        notes: editingTemplate.notes ?? undefined,
      });
    } else {
      templateForm.setFieldsValue({ activeFrom: month });
    }
  }, [templateModalOpen, editingTemplate, templateForm, month]);

  const closeTemplateModal = () => {
    if (savingTemplate) return;
    setTemplateModalOpen(false);
    setEditingTemplate(null);
  };

  const handleTemplateSubmit = async (values: TemplateFormValues) => {
    setSavingTemplate(true);
    try {
      const payload = {
        name: values.name,
        category: values.category,
        amount: Number(values.amount),
        currency: values.currency,
        activeFrom: values.activeFrom.format(MONTH_FORMAT),
        activeTo: values.activeTo ? values.activeTo.format(MONTH_FORMAT) : null,
        notes: values.notes || null,
      };
      if (editingTemplate) {
        const result = await updateCompanyCostTemplate(editingTemplate.id, payload);
        feedback.success(t('fixedCosts:messages.templateUpdateSuccess', { name: values.name }));
        if (result.prunedMonths > 0) {
          feedback.info(t('fixedCosts:messages.templateWindowPruned', { count: result.prunedMonths }));
        }
      } else {
        await createCompanyCostTemplate(payload);
        feedback.success(t('fixedCosts:messages.templateCreateSuccess', { name: values.name }));
      }
      setTemplateModalOpen(false);
      setEditingTemplate(null);
      await Promise.all([loadTemplates(), loadMonth(monthParam)]);
    } catch (error: any) {
      feedback.error(
        getApiErrorMessage(error, t, 'fixedCosts:messages.templateSaveFailed', undefined, { name: values.name }),
      );
    } finally {
      setSavingTemplate(false);
    }
  };

  const handleToggleTemplateActive = async (template: CompanyCostTemplate) => {
    setTogglingTemplateId(template.id);
    const activating = !template.isActive;
    try {
      await updateCompanyCostTemplate(template.id, { isActive: activating });
      feedback.success(
        t(
          activating
            ? 'fixedCosts:messages.templateActivateSuccess'
            : 'fixedCosts:messages.templateDeactivateSuccess',
          { name: template.name },
        ),
      );
      await Promise.all([loadTemplates(), loadMonth(monthParam)]);
    } catch (error: any) {
      feedback.error(
        getApiErrorMessage(error, t, 'fixedCosts:messages.templateStatusUpdateFailed', undefined, {
          name: template.name,
        }),
      );
    } finally {
      setTogglingTemplateId(null);
    }
  };

  const handleDeleteTemplate = async (template: CompanyCostTemplate) => {
    setDeletingTemplateId(template.id);
    try {
      await deleteCompanyCostTemplate(template.id);
      feedback.success(t('fixedCosts:messages.templateDeleteSuccess', { name: template.name }));
      await loadTemplates();
    } catch (error: any) {
      feedback.error(
        getApiErrorMessage(error, t, 'fixedCosts:messages.templateDeleteFailed', undefined, {
          name: template.name,
        }),
      );
    } finally {
      setDeletingTemplateId(null);
    }
  };

  // ── "Koszty miesiąca" derived figures ────────────────────────────
  const categoryBreakdown = useMemo(() => (
    (Object.entries(summary.byCategory) as [CompanyCostCategory, number][])
      .filter(([, amount]) => Number(amount) !== 0)
      .map(([category, amount]) => ({ category, amount: Number(amount) }))
      .sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount))
  ), [summary.byCategory]);

  const categoryTotal = useMemo(
    () => categoryBreakdown.reduce((sum, row) => sum + Math.abs(row.amount), 0) || 1,
    [categoryBreakdown],
  );

  const paidAmount = useMemo(
    () => entries.filter((entry) => entry.isPaid).reduce((sum, entry) => sum + entry.amount, 0),
    [entries],
  );
  const unpaidAmount = useMemo(
    () => entries.filter((entry) => !entry.isPaid).reduce((sum, entry) => sum + entry.amount, 0),
    [entries],
  );

  // ── Month-over-month delta (hero satellite) ──────────────────────
  const deltaBlock = useMemo(() => {
    if (prevLoading) {
      return { cls: 'is-neutral', node: <strong className="fixedcosts-satellite__muted">…</strong> };
    }
    if (!prevSummary || prevSummary.entryCount === 0) {
      return {
        cls: 'is-neutral',
        node: (
          <>
            <strong className="fixedcosts-satellite__muted">—</strong>
            <small>{t('fixedCosts:satellites.deltaNoData')}</small>
          </>
        ),
      };
    }
    const diff = summary.total - prevSummary.total;
    if (diff === 0) {
      return {
        cls: 'is-neutral',
        node: (
          <>
            <strong>{compact(0)}</strong>
            <small>{t('fixedCosts:satellites.deltaFlat')}</small>
          </>
        ),
      };
    }
    const grew = diff > 0;
    const pct = prevSummary.total > 0 ? (Math.abs(diff) / prevSummary.total) * 100 : null;
    return {
      cls: grew ? 'is-worse' : 'is-better',
      node: (
        <>
          <strong>
            {grew ? <RiseOutlined /> : <FallOutlined />}
            {grew ? '+' : '−'}
            {compact(Math.abs(diff))}
          </strong>
          {pct !== null && <small>{formatLocalizedPercent(pct, i18n.language)}</small>}
        </>
      ),
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prevLoading, prevSummary, summary.total, i18n.language, t]);

  // ── "Koszty cykliczne" derived figures ───────────────────────────
  const activeTemplates = useMemo(() => templates.filter((tpl) => tpl.isActive), [templates]);
  const activeTemplatesMonthlySum = useMemo(
    () => activeTemplates.reduce((sum, tpl) => sum + tpl.amount, 0),
    [activeTemplates],
  );

  function entryRowClassName(record: CompanyCostEntry): string {
    return record.isPaid ? 'fixedcosts-row' : 'fixedcosts-row fixedcosts-row--unpaid';
  }

  function templateRowClassName(record: CompanyCostTemplate): string {
    return record.isActive ? 'fixedcosts-row' : 'fixedcosts-row fixedcosts-row--inactive';
  }

  const entryColumns: TableColumnsType<CompanyCostEntry> = [
    {
      title: t('fixedCosts:table.name'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('fixedCosts:table.category'),
      dataIndex: 'category',
      key: 'category',
      render: (category: CompanyCostCategory) => (
        <span className="fixedcosts-table-category">
          <span className="fixedcosts-category-dot" style={{ background: CATEGORY_COLORS[category] }} />
          {t(`fixedCosts:categories.${category}`, { defaultValue: category })}
        </span>
      ),
    },
    {
      title: t('fixedCosts:table.amount'),
      key: 'amount',
      align: 'right',
      render: (_value, record) => (
        <div className="fixedcosts-table-amount">
          <div className="fixedcosts-table-amount__main">{formatCurrency(record.amount, 'PLN')}</div>
          {record.originalCurrency && record.originalCurrency !== 'PLN' && record.originalAmount != null && (
            <div className="fixedcosts-table-amount__original">
              {formatCurrency(record.originalAmount, record.originalCurrency)}
            </div>
          )}
        </div>
      ),
    },
    {
      title: t('fixedCosts:table.source'),
      dataIndex: 'source',
      key: 'source',
      render: (source: CompanyCostSource) => (
        <Tag color={SOURCE_TAG_COLOR[source] ?? 'default'}>{t(`fixedCosts:source.${source}`, { defaultValue: source })}</Tag>
      ),
    },
    {
      title: t('fixedCosts:table.invoiceNumber'),
      dataIndex: 'invoiceNumber',
      key: 'invoiceNumber',
      render: (value?: string | null) => value || '—',
    },
    {
      title: t('fixedCosts:table.paid'),
      key: 'isPaid',
      render: (_value, record) => (
        <Tag color={record.isPaid ? 'success' : 'default'}>
          {record.isPaid ? t('fixedCosts:table.paid') : t('fixedCosts:table.unpaid')}
        </Tag>
      ),
    },
    {
      title: t('fixedCosts:table.actions'),
      key: 'actions',
      width: 110,
      align: 'right',
      render: (_value, record) => (
        <Space>
          <Tooltip title={t('common:edit')}>
            <Button size="small" icon={<EditOutlined />} onClick={() => openEditEntry(record)} />
          </Tooltip>
          <Popconfirm
            title={t('fixedCosts:messages.deleteConfirmEntry', {
              name: record.name,
              month: dayjs(record.month, MONTH_FORMAT).format('MM/YYYY'),
            })}
            okText={t('common:yes')}
            cancelText={t('common:no')}
            onConfirm={() => {
              void handleDeleteEntry(record);
            }}
          >
            <Tooltip title={t('common:delete')}>
              <Button size="small" danger icon={<DeleteOutlined />} loading={deletingEntryId === record.id} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  // ── Hero + satellites (dark navy hero, Profitability-style) ──────
  const heroSection = (
    <section className="fixedcosts-overview">
      <div className="fixedcosts-overview__grid">
        <article className="fixedcosts-hero">
          <header className="fixedcosts-hero__header">
            <span className="fixedcosts-hero__kicker">{t('fixedCosts:hero.kicker')}</span>
            <div className="fixedcosts-hero__controls">
              <MonthPicker
                value={month}
                onChange={(date) => date && setMonth(date)}
                format={MONTH_FORMAT}
                allowClear={false}
              />
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreateEntry}>
                {t('fixedCosts:month.addCost')}
              </Button>
            </div>
          </header>
          <strong className="fixedcosts-hero__value">{compact(summary.total)}</strong>
          <span className="fixedcosts-hero__exact">{formatCurrency(summary.total, 'PLN')}</span>
          <div className="fixedcosts-hero__footer">
            <span>
              <small>{t('fixedCosts:month.summaryCount')}</small>
              <strong>{summary.entryCount}</strong>
            </span>
            <span>
              <small>{t('fixedCosts:hero.paid')}</small>
              <strong>{compact(paidAmount)}</strong>
            </span>
            <span>
              <small>{t('fixedCosts:hero.unpaid')}</small>
              <strong>{compact(unpaidAmount)}</strong>
            </span>
          </div>
        </article>

        <div className="fixedcosts-satellites">
          <article className="fixedcosts-satellite is-paid">
            <span>{t('fixedCosts:satellites.paidTitle')}</span>
            <strong>{compact(paidAmount)}</strong>
            <small>{formatCurrency(paidAmount, 'PLN')}</small>
          </article>
          <article className="fixedcosts-satellite is-due">
            <span>{t('fixedCosts:satellites.dueTitle')}</span>
            <strong>{compact(unpaidAmount)}</strong>
            <small>{formatCurrency(unpaidAmount, 'PLN')}</small>
          </article>
          <article className={`fixedcosts-satellite is-delta ${deltaBlock.cls}`}>
            <span>{t('fixedCosts:satellites.deltaTitle')}</span>
            {deltaBlock.node}
          </article>
        </div>
      </div>
    </section>
  );

  const categoryBreakdownCard = (
    <Card className="fixedcosts-category-card" size="small" title={t('fixedCosts:categoryBreakdown.title')}>
      {categoryBreakdown.length === 0 ? (
        <Empty description={t('fixedCosts:categoryBreakdown.empty')} image={Empty.PRESENTED_IMAGE_SIMPLE} />
      ) : (
        categoryBreakdown.map(({ category, amount }) => {
          const share = (Math.abs(amount) / categoryTotal) * 100;
          return (
            <div className="fixedcosts-category-row" key={category}>
              <div className="fixedcosts-category-row__label">
                <span className="fixedcosts-category-dot" style={{ background: CATEGORY_COLORS[category] }} />
                <span className="fixedcosts-category-row__name">
                  {t(`fixedCosts:categories.${category}`, { defaultValue: category })}
                </span>
              </div>
              <div className="fixedcosts-category-row__bar">
                <div
                  className="fixedcosts-category-row__bar-fill"
                  style={{ width: `${Math.min(100, Math.max(2, share))}%`, background: CATEGORY_COLORS[category] }}
                />
              </div>
              <div className="fixedcosts-category-row__value">
                <span className="fixedcosts-category-row__amount">{formatCurrency(amount, 'PLN')}</span>
                <span className="fixedcosts-category-row__share">{formatLocalizedNumber(share, 0, i18n.language)}%</span>
              </div>
            </div>
          );
        })
      )}
    </Card>
  );

  const monthTab = (
    <div className="fixedcosts-page">
      {heroSection}
      {categoryBreakdownCard}

      <div className="fixedcosts-table-card">
        <Table<CompanyCostEntry>
          columns={entryColumns}
          dataSource={entries}
          rowKey="id"
          rowClassName={entryRowClassName}
          loading={entriesLoading}
          pagination={{ pageSize: 20 }}
          locale={{
            emptyText: (
              <div className="fixedcosts-empty">
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={(
                    <span>
                      {t('fixedCosts:month.empty')}
                      <br />
                      <span className="fixedcosts-empty__hint">{t('fixedCosts:month.emptyHint')}</span>
                    </span>
                  )}
                />
                <Button type="primary" icon={<PlusOutlined />} onClick={openCreateEntry}>
                  {t('fixedCosts:month.addCost')}
                </Button>
              </div>
            ),
          }}
        />
      </div>

      <Modal
        title={editingEntry ? t('fixedCosts:entryModal.editTitle') : t('fixedCosts:entryModal.addTitle')}
        open={entryModalOpen}
        onCancel={closeEntryModal}
        onOk={() => entryForm.submit()}
        okText={t('common:save')}
        cancelText={t('common:cancel')}
        confirmLoading={savingEntry}
        maskClosable={false}
        destroyOnClose
      >
        <Form
          form={entryForm}
          layout="vertical"
          onFinish={handleEntrySubmit}
          initialValues={{ currency: 'PLN', isPaid: false }}
        >
          <Form.Item
            name="name"
            label={t('fixedCosts:entryModal.name')}
            rules={[{ required: true, message: t('fixedCosts:entryModal.nameRequired') }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="category"
            label={t('fixedCosts:entryModal.category')}
            rules={[{ required: true, message: t('fixedCosts:entryModal.categoryRequired') }]}
          >
            <Select
              options={COMPANY_COST_CATEGORIES.map((category) => ({
                value: category,
                label: t(`fixedCosts:categories.${category}`),
              }))}
            />
          </Form.Item>

          <Space.Compact style={{ width: '100%' }}>
            <Form.Item
              name="amount"
              label={t('fixedCosts:entryModal.amount')}
              style={{ flex: 1, marginRight: 8 }}
              rules={[
                { required: true, message: t('fixedCosts:entryModal.amountRequired') },
                {
                  type: 'number',
                  min: 0.01,
                  max: MAX_COMPANY_COST_AMOUNT,
                  message: t('fixedCosts:entryModal.amountRange', { max: MAX_COMPANY_COST_AMOUNT }),
                },
              ]}
            >
              <InputNumber style={{ width: '100%' }} step={0.01} precision={2} />
            </Form.Item>
            <Form.Item name="currency" label={t('fixedCosts:entryModal.currency')} style={{ width: 120 }}>
              <Select options={COMPANY_COST_CURRENCIES.map((currency) => ({ value: currency, label: currency }))} />
            </Form.Item>
          </Space.Compact>

          <Form.Item name="invoiceNumber" label={t('fixedCosts:entryModal.invoiceNumber')}>
            <Input />
          </Form.Item>

          <Form.Item name="isPaid" label={t('fixedCosts:entryModal.isPaid')} valuePropName="checked">
            <Switch />
          </Form.Item>

          {entryIsPaid && (
            <Form.Item name="paymentDate" label={t('fixedCosts:entryModal.paymentDate')}>
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>
          )}

          <Form.Item name="notes" label={t('fixedCosts:entryModal.notes')}>
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );

  // ── "Koszty cykliczne" table ──────────────────────────────────────
  const templateColumns: TableColumnsType<CompanyCostTemplate> = [
    {
      title: t('fixedCosts:table.name'),
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: t('fixedCosts:table.category'),
      dataIndex: 'category',
      key: 'category',
      render: (category: CompanyCostCategory) => (
        <span className="fixedcosts-table-category">
          <span className="fixedcosts-category-dot" style={{ background: CATEGORY_COLORS[category] }} />
          {t(`fixedCosts:categories.${category}`, { defaultValue: category })}
        </span>
      ),
    },
    {
      title: t('fixedCosts:table.amount'),
      key: 'amount',
      align: 'right',
      render: (_value, record) => (
        <div className="fixedcosts-table-amount">
          <div className="fixedcosts-table-amount__main">{formatCurrency(record.amount, 'PLN')}</div>
          {record.originalCurrency && record.originalCurrency !== 'PLN' && record.originalAmount != null && (
            <div className="fixedcosts-table-amount__original">
              {formatCurrency(record.originalAmount, record.originalCurrency)}
            </div>
          )}
        </div>
      ),
    },
    {
      title: `${t('fixedCosts:templates.activeFrom')} / ${t('fixedCosts:templates.activeTo')}`,
      key: 'activeRange',
      render: (_value, record) => {
        let progress: number | null = null;
        if (record.activeTo) {
          const from = dayjs(record.activeFrom, MONTH_FORMAT);
          const to = dayjs(record.activeTo, MONTH_FORMAT);
          const totalMonths = Math.max(1, to.diff(from, 'month'));
          const elapsed = Math.min(totalMonths, Math.max(0, dayjs().diff(from, 'month')));
          progress = Math.min(100, Math.max(0, (elapsed / totalMonths) * 100));
        }
        return (
          <div className="fixedcosts-range">
            <div className="fixedcosts-range__text">{record.activeFrom} — {record.activeTo || '—'}</div>
            {progress !== null && (
              <div className="fixedcosts-range__bar">
                <div className="fixedcosts-range__bar-fill" style={{ width: `${progress}%` }} />
              </div>
            )}
          </div>
        );
      },
    },
    {
      title: t('fixedCosts:templates.status'),
      key: 'status',
      render: (_value, record) => (
        <Tag color={record.isActive ? 'success' : 'default'}>
          {record.isActive ? t('fixedCosts:templates.active') : t('fixedCosts:templates.inactive')}
        </Tag>
      ),
    },
    {
      title: t('fixedCosts:table.actions'),
      key: 'actions',
      width: 170,
      align: 'right',
      render: (_value, record) => (
        <Space>
          <Tooltip title={t('common:edit')}>
            <Button size="small" icon={<EditOutlined />} onClick={() => openEditTemplate(record)} />
          </Tooltip>
          <Button
            size="small"
            onClick={() => handleToggleTemplateActive(record)}
            loading={togglingTemplateId === record.id}
          >
            {record.isActive ? t('fixedCosts:templates.deactivate') : t('fixedCosts:templates.activate')}
          </Button>
          <Popconfirm
            title={t('fixedCosts:templates.deleteConfirmTitle')}
            description={t('fixedCosts:templates.deleteConfirmDescription')}
            okText={t('common:yes')}
            cancelText={t('common:no')}
            onConfirm={() => {
              void handleDeleteTemplate(record);
            }}
          >
            <Tooltip title={t('common:delete')}>
              <Button size="small" danger icon={<DeleteOutlined />} loading={deletingTemplateId === record.id} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const templatesSummaryStrip = (
    <div className="fixedcosts-templates-summary">
      <div className="fixedcosts-templates-summary__stat">
        <span>{t('fixedCosts:templatesSummary.activeCount')}</span>
        <strong>{activeTemplates.length}</strong>
      </div>
      <div className="fixedcosts-templates-summary__stat">
        <span>{t('fixedCosts:templatesSummary.activeMonthlySum')}</span>
        <strong>{compact(activeTemplatesMonthlySum)}</strong>
      </div>
      <div className="fixedcosts-templates-summary__hint">
        <InfoCircleOutlined />
        <span>{t('fixedCosts:templates.infoAlert')}</span>
      </div>
    </div>
  );

  const templatesTab = (
    <div className="fixedcosts-page">
      {templatesSummaryStrip}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreateTemplate}>
          {t('fixedCosts:templates.addTemplate')}
        </Button>
      </div>

      <div className="fixedcosts-table-card">
        <Table<CompanyCostTemplate>
          columns={templateColumns}
          dataSource={templates}
          rowKey="id"
          rowClassName={templateRowClassName}
          loading={templatesLoading}
          pagination={{ pageSize: 20 }}
          locale={{
            emptyText: (
              <div className="fixedcosts-empty">
                <Empty
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  description={(
                    <span>
                      {t('fixedCosts:templates.empty')}
                      <br />
                      <span className="fixedcosts-empty__hint">{t('fixedCosts:templates.emptyHint')}</span>
                    </span>
                  )}
                />
                <Button type="primary" icon={<PlusOutlined />} onClick={openCreateTemplate}>
                  {t('fixedCosts:templates.addTemplate')}
                </Button>
              </div>
            ),
          }}
        />
      </div>

      <Modal
        title={editingTemplate ? t('fixedCosts:templateModal.editTitle') : t('fixedCosts:templateModal.addTitle')}
        open={templateModalOpen}
        onCancel={closeTemplateModal}
        onOk={() => templateForm.submit()}
        okText={t('common:save')}
        cancelText={t('common:cancel')}
        confirmLoading={savingTemplate}
        maskClosable={false}
        destroyOnClose
      >
        <Form
          form={templateForm}
          layout="vertical"
          onFinish={handleTemplateSubmit}
          initialValues={{ currency: 'PLN' }}
        >
          <Form.Item
            name="name"
            label={t('fixedCosts:templateModal.name')}
            rules={[{ required: true, message: t('fixedCosts:templateModal.nameRequired') }]}
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="category"
            label={t('fixedCosts:templateModal.category')}
            rules={[{ required: true, message: t('fixedCosts:templateModal.categoryRequired') }]}
          >
            <Select
              options={COMPANY_COST_CATEGORIES.map((category) => ({
                value: category,
                label: t(`fixedCosts:categories.${category}`),
              }))}
            />
          </Form.Item>

          <Space.Compact style={{ width: '100%' }}>
            <Form.Item
              name="amount"
              label={t('fixedCosts:templateModal.amount')}
              style={{ flex: 1, marginRight: 8 }}
              rules={[
                { required: true, message: t('fixedCosts:templateModal.amountRequired') },
                {
                  type: 'number',
                  min: 0.01,
                  max: MAX_COMPANY_COST_AMOUNT,
                  message: t('fixedCosts:templateModal.amountRange', { max: MAX_COMPANY_COST_AMOUNT }),
                },
              ]}
            >
              <InputNumber style={{ width: '100%' }} step={0.01} precision={2} />
            </Form.Item>
            <Form.Item name="currency" label={t('fixedCosts:templateModal.currency')} style={{ width: 120 }}>
              <Select options={COMPANY_COST_CURRENCIES.map((currency) => ({ value: currency, label: currency }))} />
            </Form.Item>
          </Space.Compact>

          <Space.Compact style={{ width: '100%' }}>
            <Form.Item
              name="activeFrom"
              label={t('fixedCosts:templateModal.activeFrom')}
              style={{ flex: 1, marginRight: 8 }}
              rules={[{ required: true, message: t('fixedCosts:templateModal.activeFromRequired') }]}
            >
              <MonthPicker style={{ width: '100%' }} format={MONTH_FORMAT} />
            </Form.Item>
            <Form.Item
              name="activeTo"
              label={t('fixedCosts:templateModal.activeTo')}
              style={{ flex: 1 }}
              tooltip={t('fixedCosts:templateModal.activeToHint')}
            >
              <MonthPicker style={{ width: '100%' }} format={MONTH_FORMAT} allowClear />
            </Form.Item>
          </Space.Compact>

          <Form.Item name="notes" label={t('fixedCosts:templateModal.notes')}>
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );

  const tabItems = [
    {
      key: 'month',
      label: t('fixedCosts:tabs.month'),
      children: monthTab,
    },
    {
      key: 'templates',
      label: t('fixedCosts:tabs.templates'),
      children: templatesTab,
    },
  ];

  return (
    <div>
      <Tabs items={tabItems} defaultActiveKey="month" />
    </div>
  );
};

export default FixedCosts;
